'use client'

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { Box, Container } from '@mui/material'

import { useWallet } from '@/src/hooks/wallet'
import { formatCurrency } from '@/src/utils/currency/formatCurrency'
import { formatTransactionForDisplay } from '@/src/utils/wallet'
import { SOURCE_TYPE_ICON_TYPES } from '@/src/constants/wallet'
import HeaderContainer from '@/src/components/common/Header'

import { WalletDesktopPage } from './wallet'
import { WalletFAQ } from '../../mobile/wallet/walletFAQ'

// Dynamically load SignInModal
const SignInModalWrapper = dynamic(
    () =>
        import(
            /* webpackChunkName: "signin-modal" */ '@/src/components/common/flyinHeader/SignInModal/SignInModalWrapper'
        ),
    { ssr: false }
)

const WalletDesktopPageWrapper: React.FC = () => {
    const router = useRouter()
    const locale = useLocale()
    const t = useTranslations('wallet')
    const tCommon = useTranslations('common')

    // Track mount state
    const [isMounted, setIsMounted] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [tooltipOpen, setTooltipOpen] = useState(false)

    // Use wallet hook
    const wallet = useWallet()

    // Check for RTL
    const isRtl = locale === 'ar'

    // Format transactions for display (business logic in logic layer)
    const formattedTransactions = useMemo(() => {
        if (!wallet.transactions || wallet.transactions.length === 0) {
            return []
        }
        try {
            const formatted = wallet.transactions.map(txn => formatTransactionForDisplay(txn, t))
            // Add icon type based on source type (no tab checking needed)
            return formatted.map(txn => ({
                ...txn,
                iconType: (txn.rawSourceType && SOURCE_TYPE_ICON_TYPES[txn.rawSourceType]) || 'card'
            }))
        } catch (error) {
            console.error('Error formatting transactions:', error)
            return []
        }
    }, [wallet.transactions, t])

    // Handler for FAQ section
    const handleOpenFAQ = useCallback(() => {
        // Scroll to FAQ section (which is rendered below)
        const faqElement = document.getElementById('wallet-faq-section')
        if (faqElement) {
            faqElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }, [])

    // Initialize on mount
    useEffect(() => {
        if (typeof window === 'undefined') return
        setIsMounted(true)
    }, [])

    // Show sign-in modal if not logged in
    useEffect(() => {
        if (isMounted && !wallet.isLoggedIn) {
            setModalOpen(true)
        }
    }, [isMounted, wallet.isLoggedIn])

    // Handlers
    const handleModalClose = useCallback(() => {
        setModalOpen(false)
        // Redirect if still not logged in
        if (!wallet.isLoggedIn) {
            router.push(`/${locale}`)
        }
    }, [wallet.isLoggedIn, router, locale])

    const handleTooltipOpen = useCallback(() => {
        setTooltipOpen(true)
    }, [])

    const handleTooltipClose = useCallback(() => {
        setTooltipOpen(false)
    }, [])

    // Format amount helper
    const formatAmount = useCallback(
        (amount: number, currency: string) =>
            formatCurrency({
                amount,
                currency,
                locale,
                precision: 2
            }),
        [locale]
    )

    // Translate error messages
    const translatedBalanceError = useMemo(() => {
        if (!wallet.balanceError) return null
        // If it's the hardcoded message, use translation
        if (wallet.balanceError === 'Unable to fetch wallet balance') {
            return t('errors.balanceFetchFailed')
        }
        return wallet.balanceError
    }, [wallet.balanceError, t])

    const translatedTransactionsError = useMemo(() => {
        if (!wallet.transactionsError) return null
        // If it's the hardcoded message, use translation
        if (wallet.transactionsError === 'Unable to fetch transactions') {
            return t('errors.transactionsFetchFailed')
        }
        return wallet.transactionsError
    }, [wallet.transactionsError, t])

    // Don't render until mounted
    if (!isMounted) {
        return null
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'white'
            }}
        >
            {/* Desktop Header */}
            <HeaderContainer />

            {/* Main Content */}
            <Box sx={{ flex: 1, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <WalletDesktopPage
                        displayBalance={wallet.displayBalance}
                        displayCurrency={wallet.displayCurrency}
                        domainBalances={wallet.domainBalances}
                        hasMultipleDomainBalances={wallet.hasMultipleDomainBalances}
                        formattedTransactions={formattedTransactions}
                        hasMoreTransactions={wallet.hasMoreTransactions}
                        activeTab={wallet.activeTab}
                        onTabChange={wallet.setActiveTab}
                        isLoadingBalance={wallet.isLoadingBalance}
                        isLoadingTransactions={wallet.isLoadingTransactions}
                        isFetchingMore={wallet.isFetchingTransactions}
                        balanceError={translatedBalanceError}
                        transactionsError={translatedTransactionsError}
                        onLoadMore={wallet.loadMoreTransactions}
                        onRefresh={wallet.refreshWallet}
                        isTooltipOpen={tooltipOpen}
                        onTooltipOpen={handleTooltipOpen}
                        onTooltipClose={handleTooltipClose}
                        onOpenFAQ={handleOpenFAQ}
                        formatAmount={formatAmount}
                        t={t}
                        tCommon={tCommon}
                        locale={locale}
                        isRtl={isRtl}
                    />
                </Container>

                {/* FAQ Section */}
                <Container maxWidth="lg" id="wallet-faq-section">
                    <WalletFAQ />
                </Container>
            </Box>

            {/* Sign In Modal */}
            {modalOpen && <SignInModalWrapper open={modalOpen} onClose={handleModalClose} />}
        </Box>
    )
}

export { WalletDesktopPageWrapper }
export default WalletDesktopPageWrapper
